import AppAvatar from '@app/components/ui/Avatar';
import { getInitials } from '@app/utils/extractFullnameInitials';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { BsIncognito } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import {
  AttachmentAddedNotificationType,
  AttachmentRemovedNotificationType,
  VisitAssignmentAddedNotificationType,
  VisitAssignmentRemovedNotificationType,
  VisitCheckInReminderNotificationType,
  VisitCheckedInNotificationType,
  VisitRescheduledNotificationType,
  VisitUpdatedNotificationType,
} from '../../types/notifications';
import { LIST_ITEM_ANIMATIONS } from '../consts/motionAnimations';

// dayjs.extend(relativeTime);

type UpdatedVisitWidgetNotificationCardProps = {
  notificationDetails:
    | VisitUpdatedNotificationType
    | VisitAssignmentAddedNotificationType
    | VisitAssignmentRemovedNotificationType
    | VisitCheckInReminderNotificationType
    | VisitCheckedInNotificationType
    | VisitRescheduledNotificationType
    | AttachmentAddedNotificationType
    | AttachmentRemovedNotificationType;
  onClick: (id: number) => void;
};

const UpdatedVisitWidgetNotificationCard = ({
  notificationDetails,
  onClick,
}: UpdatedVisitWidgetNotificationCardProps) => {
  const { t } = useTranslation();
  const meta = notificationDetails.metadata?.[0];
  const timeAgo = (iso: string) => dayjs(iso).fromNow();

  const caregiverFullname = meta?.caregiverNames?.[0];
  const fullnameLetters = getInitials(caregiverFullname || '', { fallback: '' });

  return (
    <motion.li key={notificationDetails.notificationId} className="overflow-hidden" {...LIST_ITEM_ANIMATIONS}>
      <div className={`group flex flex-col px-4 py-3 hover:bg-slate-50 cursor-pointer`}>
        <div
          onClick={() => onClick(notificationDetails.notificationId)}
          className="flex w-full items-start gap-3 text-left"
        >
          {caregiverFullname ? (
            <AppAvatar isSelected={true} fullName={caregiverFullname} value={fullnameLetters} />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-700">
              <BsIncognito fontSize={20} />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-700 line-clamp-2">{notificationDetails.title}</h3>

                <Tag bordered={false} color="blue">
                  <span className="font-light">{t('notifications-tag-pending')}</span>
                </Tag>
              </div>

              <span className="ml-2 shrink-0 text-xs text-slate-400">{timeAgo(notificationDetails.createdAt)}</span>
            </div>

            <p className="mt-0.5 line-clamp-4 text-xs text-slate-600">{notificationDetails.message}</p>
          </div>

          {!notificationDetails.isRead && <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500" />}
        </div>
      </div>
    </motion.li>
  );
};

export default UpdatedVisitWidgetNotificationCard;
